/**
 * Camera rigs (spec §2). A rig turns per-frame inputs into CameraParams. The
 * FixedTeamRig is the 02 §7 camera (pose zero) for matches, spectating and the
 * training lane; the FollowRig tracks the local player and yaws toward aim
 * in the open world. Free third-person camera: the yaw is driven by the look
 * device alone (the aim is a fixed reticle through this camera, so it must
 * never feed back into the yaw). Everything is team-local; mirroring stays in
 * toTeamLocal.
 */
import { dirToWorld } from '../../sim/arena.js'
import type { Arena, Team, Vec2 } from '../../sim/types.js'
import { DEFAULT_CAMERA, ZERO_POSE, toTeamLocal, type CameraParams, type CameraPose } from './camera.js'

export interface LensParams { back: number; height: number; pitchDeg: number; hfovDeg: number; near: number; shoulder: number; followLag: number; aimLead: number; standoff: number }
export const DEFAULT_LENS: Readonly<LensParams> = Object.freeze({ back: DEFAULT_CAMERA.back, height: DEFAULT_CAMERA.height, pitchDeg: DEFAULT_CAMERA.pitchDeg, hfovDeg: DEFAULT_CAMERA.hfovDeg, near: DEFAULT_CAMERA.near, shoulder: 0.6, followLag: 0.12, aimLead: 1.5, standoff: 3 })

/** S10 (spec §3): the lens for an arena — `arena.cam` over DEFAULT_LENS; the follow-rig terms (shoulder, lag, lead, standoff) stay. */
export function lensFor(arena: Arena): LensParams { return { ...DEFAULT_LENS, ...(arena.cam ?? {}) } }

/** `lookYaw`: radians of camera turn this frame from the look device (pointer-lock mouse, right stick);
 *  +ve turns toward screen right. `aimAngle` only seeds the yaw on a snap. */
export interface RigInput { dt: number; localPos: Vec2 | null; aimAngle: number; lookYaw: number; team: Team; arenaLength: number }
export interface CameraRig { readonly kind: 'fixed' | 'follow'; lens: LensParams; update(i: RigInput): CameraParams; snap(): void }

export const wrapAngle = (a: number): number => { let r = a % (2 * Math.PI); if (r > Math.PI) r -= 2 * Math.PI; if (r <= -Math.PI) r += 2 * Math.PI; return r }

/** Screen-space move → team-local move for a camera yawed by `yaw` (forward = (sin yaw, cos yaw)). */
export function rotateMove(mx: number, my: number, yaw: number): { mx: number; my: number } {
  const c = Math.cos(yaw), s = Math.sin(yaw)
  return { mx: mx * c + my * s, my: -mx * s + my * c }
}

/** World aim bearing (radians, atan2(z, x)) → team-local yaw (0 = +z local, +ve toward +x local). */
export function aimToLocalYaw(aimAngle: number, team: Team): number {
  const d = dirToWorld({ x: Math.cos(aimAngle), z: Math.sin(aimAngle) }, team) // involution: world → local
  return Math.atan2(d.x, d.z)
}

/** Dev/iteration override `?cam=back,height,pitchDeg,hfovDeg` (any trailing value may be omitted). Null when absent or unparsable. */
export function parseCamOverride(raw: string | null | undefined): Partial<LensParams> | null {
  if (!raw) return null
  const n = raw.split(',').map((v) => Number(v.trim()))
  if (!n.length || n.some((v) => !Number.isFinite(v))) return null
  const out: Partial<LensParams> = {}
  const keys: (keyof LensParams)[] = ['back', 'height', 'pitchDeg', 'hfovDeg']
  n.slice(0, 4).forEach((v, i) => { (out as Record<string, number>)[keys[i]] = v })
  return out
}

const lensToCam = (l: LensParams, pose: CameraPose): CameraParams => ({ back: l.back, height: l.height, pitchDeg: l.pitchDeg, hfovDeg: l.hfovDeg, near: l.near, pose })

export class FixedTeamRig implements CameraRig {
  readonly kind = 'fixed' as const
  constructor(public lens: LensParams = { ...DEFAULT_LENS }) {}
  update(): CameraParams { return lensToCam(this.lens, ZERO_POSE) }
  snap(): void {}
}

export class FollowRig implements CameraRig {
  readonly kind = 'follow' as const
  private pose: CameraPose | null = null
  private pendingSnap = true
  constructor(public lens: LensParams = { ...DEFAULT_LENS }) {}
  snap(): void { this.pendingSnap = true }
  update(i: RigInput): CameraParams {
    if (!i.localPos) return lensToCam(this.lens, this.pose ?? ZERO_POSE)
    const l = toTeamLocal(i.localPos.x, i.localPos.z, i.team, i.arenaLength)
    // Yaw: snap seeds it from the aim bearing (initial facing); afterwards only look input turns it.
    const yaw = this.pendingSnap || !this.pose ? aimToLocalYaw(i.aimAngle, i.team) : wrapAngle(this.pose.yaw + i.lookYaw)
    // Position: player + shoulder (right = (cos, -sin)) + (aimLead − standoff) along the camera's own forward (sin, cos).
    // `standoff` keeps the athlete `back + standoff` metres from the camera — 3 m reproduces the corridor's own-spawn framing (02 §7 table).
    const c = Math.cos(yaw), s = Math.sin(yaw)
    const fwd = this.lens.aimLead - this.lens.standoff
    const tx = l.lx + this.lens.shoulder * c + fwd * s
    const tz = l.lz - this.lens.shoulder * s + fwd * c
    if (this.pendingSnap || !this.pose || this.lens.followLag <= 0) { this.pose = { x: tx, z: tz, yaw }; this.pendingSnap = false }
    else {
      const k = 1 - Math.exp(-i.dt / this.lens.followLag)
      this.pose = { x: this.pose.x + (tx - this.pose.x) * k, z: this.pose.z + (tz - this.pose.z) * k, yaw }
    }
    return lensToCam(this.lens, this.pose)
  }
}
